// ============================================================================
// California HRIS Forms & Mandatory Notices
// ============================================================================

import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// CALIFORNIA DE-4 FORM CONFIGURATION
// ============================================================================

export const CA_DE4_FILING_STATUS = [
  { value: 'single', label: 'Single or Married (with two or more incomes)', code: 'A' },
  { value: 'married_one_income', label: 'Married (one income)', code: 'B' },
  { value: 'head_of_household', label: 'Head of Household', code: 'C' },
];

export const CA_DE4_FORM = {
  formCode: 'DE-4',
  formName: "Employee's Withholding Allowance Certificate",
  description: 'California requires employers to use this form for state income tax withholding. Complete this form so your employer can withhold the correct California state income tax from your pay.',
  category: 'state_tax',
  jurisdiction: 'CA',
  agency: 'Employment Development Department (EDD)',
  version: '2024',
  sections: [
    {
      id: 'personal',
      title: 'Personal Information',
      fields: ['firstName', 'middleInitial', 'lastName', 'ssn', 'address', 'city', 'state', 'zip'],
    },
    {
      id: 'filing_status',
      title: 'Filing Status',
      fields: ['filingStatus'],
    },
    {
      id: 'allowances',
      title: 'Withholding Allowances',
      fields: ['regularAllowances', 'additionalAllowances', 'totalAllowances'],
    },
    {
      id: 'additional',
      title: 'Additional Withholding',
      fields: ['additionalWithholding'],
    },
    {
      id: 'exemption',
      title: 'Exemption from Withholding',
      fields: ['exempt', 'exemptReason'],
    },
  ],
  instructions: `
INSTRUCTIONS FOR CALIFORNIA WITHHOLDING

Filing Status:
• Use Filing Status A if you are single or if you are married/RDP (Registered Domestic Partner) with two or more incomes
• Use Filing Status B if you are married/RDP with one income
• Use Filing Status C if you qualify as Head of Household

Regular Withholding Allowances:
• Yourself: 1 allowance
• If married or RDP and not filing separately: 1 additional allowance
• Allowances for dependents: 1 per dependent

Additional Allowances:
• You may claim additional allowances if you had large deductions itemized on your California tax return
• See DE-4 instructions for the Additional Withholding Allowance Worksheet

Exemption from Withholding:
• You may claim exempt if you had no California income tax liability last year AND expect none this year
• Exemption expires February 15 of the following year
  `,
};

// ============================================================================
// CALIFORNIA WAGE THEFT PREVENTION NOTICE (WTPN)
// Labor Code Section 2810.5
// ============================================================================

export const CA_WAGE_THEFT_PREVENTION_NOTICE = {
  noticeCode: 'CA_WTPN',
  title: 'Wage Theft Prevention Notice',
  legalReference: 'California Labor Code Section 2810.5',
  category: 'state_notices',
  jurisdiction: 'CA',
  requiresAck: true,
  requiresSignature: false,
  providedAt: 'hire',
  content: `
NOTICE TO EMPLOYEE
Labor Code section 2810.5

EMPLOYEE INFORMATION
[To be completed by employer for each employee]

1. Employee Name: ____________________
2. Start Date: ____________________

EMPLOYER INFORMATION
3. Employer Legal Name: ____________________
4. Employer DBA (if applicable): ____________________
5. Physical Address: ____________________
6. Mailing Address (if different): ____________________
7. Employer Phone Number: ____________________

WAGE INFORMATION
8. Rate(s) of Pay: $________ per ________
9. Overtime Rate(s) (if applicable): $________ per ________
10. Allowances, if any, claimed as part of minimum wage (meals, lodging): ____________________

PAY INFORMATION
11. Regular Payday: ____________________
12. Pay Basis: ☐ Hour  ☐ Shift  ☐ Day  ☐ Week  ☐ Salary  ☐ Piece  ☐ Commission  ☐ Other

WORKERS' COMPENSATION
13. Workers' Compensation Insurance Carrier: ____________________
14. Policy Number: ____________________
15. Phone Number: ____________________

The employer agrees to provide the employee a copy of this notice within seven days of any changes to the information contained in this notice.

I acknowledge that I have received this notice.

Employee Signature: ____________________ Date: ____________________
  `,
  fields: [
    { name: 'employeeName', label: 'Employee Name', type: 'text', required: true },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'rateOfPay', label: 'Rate of Pay', type: 'currency', required: true },
    { name: 'payBasis', label: 'Pay Basis', type: 'select', options: ['Hour', 'Shift', 'Day', 'Week', 'Salary', 'Piece', 'Commission', 'Other'], required: true },
    { name: 'regularPayday', label: 'Regular Payday', type: 'text', required: true },
    { name: 'overtimeRate', label: 'Overtime Rate', type: 'currency', required: false },
    { name: 'allowances', label: 'Allowances claimed as part of minimum wage', type: 'text', required: false },
  ],
};

// ============================================================================
// CALIFORNIA MANDATORY NOTICES (Read & Acknowledge)
// ============================================================================

export const CA_MANDATORY_NOTICES = [
  {
    noticeCode: 'CA_PAID_SICK_LEAVE',
    title: 'Paid Sick Leave Notice',
    legalReference: 'California Labor Code Section 246',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: PAID SICK LEAVE

Effective January 1, 2024, California law provides most employees with paid sick leave.

ACCRUAL:
• You accrue paid sick leave at a rate of at least 1 hour for every 30 hours worked
• Accrual begins on your first day of employment
• You may use accrued sick leave beginning on the 90th day of employment

AMOUNT:
• You may use up to 40 hours or 5 days of paid sick leave per year (whichever is greater)
• Unused sick leave carries over to the following year (up to 80 hours or 10 days)

ACCEPTABLE USES:
• Diagnosis, care, or treatment of your own health condition or a family member's
• Preventive care for yourself or a family member
• If you are a victim of domestic violence, sexual assault, or stalking

FAMILY MEMBERS INCLUDE:
• Child (biological, adopted, foster, stepchild, legal ward, or child of domestic partner)
• Parent (biological, adoptive, foster, stepparent, legal guardian, or parent of spouse/domestic partner)
• Spouse or registered domestic partner
• Grandparent, grandchild, or sibling
• Designated person (one person per 12-month period)

RETALIATION PROHIBITED:
Your employer is prohibited from retaliating against you for requesting or using paid sick leave.

For more information, contact the California Labor Commissioner at www.dir.ca.gov/dlse
    `,
  },
  {
    noticeCode: 'CA_MIN_WAGE',
    title: 'California Minimum Wage Notice',
    legalReference: 'California Labor Code Section 1182.12',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
OFFICIAL NOTICE: CALIFORNIA MINIMUM WAGE

Effective January 1, 2024

MINIMUM WAGE RATES:
• All Employers: $16.00 per hour

Note: Some cities and counties have higher local minimum wages. Check with your local government for applicable rates.

FAST FOOD WORKERS (AB 1228):
• Effective April 1, 2024: $20.00 per hour for fast food workers at national chains

HEALTHCARE WORKERS (SB 525):
• Minimum wages for healthcare workers vary based on facility type and size
• See DLSE guidance for specific rates

OVERTIME:
• Overtime (1.5x regular rate): Over 8 hours/day or 40 hours/week
• Double time (2x regular rate): Over 12 hours/day or over 8 hours on 7th consecutive day

EXEMPT EMPLOYEES:
• Minimum salary for exempt employees: $66,560/year ($5,546.67/month)

For more information: www.dir.ca.gov/dlse/
California Department of Industrial Relations
Division of Labor Standards Enforcement
    `,
  },
  {
    noticeCode: 'CA_WORKERS_COMP',
    title: "Workers' Compensation Rights",
    legalReference: 'California Labor Code Section 3550',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES—INJURIES CAUSED BY WORK

You may be entitled to workers' compensation benefits if you are injured or become ill because of your job. Workers' compensation covers most work-related physical or mental injuries and illnesses.

WHAT SHOULD I DO IF I AM INJURED?
1. Report the injury to your employer immediately by telling your supervisor
2. Get emergency treatment if needed
3. Your employer is required to provide a claim form within one working day after learning of your injury
4. Fill out and return the claim form

BENEFITS YOU MAY RECEIVE:
• Medical Care: Paid for by your employer to help you recover from an injury or illness caused by work
• Temporary Disability (TD) Benefits: Payments if you lose wages because your injury prevents you from doing your usual job while recovering
• Permanent Disability (PD) Benefits: Payments if you don't recover completely
• Supplemental Job Displacement Benefit: A voucher to help pay for retraining or skill enhancement if you don't recover completely and don't return to work for your employer
• Death Benefits: Payments to your spouse, children, or other dependents if you die from a work-related injury or illness

ANTI-RETALIATION:
It is illegal for your employer to punish or fire you for having a work injury or illness, for filing a claim, or testifying in another person's workers' compensation case.

For more information, contact the Division of Workers' Compensation:
www.dwc.ca.gov or 1-800-736-7401
    `,
  },
  {
    noticeCode: 'CA_UI_NOTICE',
    title: 'Unemployment Insurance (UI) Notice',
    legalReference: 'California Unemployment Insurance Code',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: UNEMPLOYMENT INSURANCE BENEFITS

Unemployment Insurance (UI) provides temporary payments to workers who lose their jobs through no fault of their own.

ELIGIBILITY:
You may be eligible for UI benefits if you:
• Are unemployed or working reduced hours
• Earned enough wages during the base period
• Are physically able to work
• Are available for work
• Are ready and willing to accept work immediately
• Are actively looking for work

HOW TO FILE A CLAIM:
• Online: www.edd.ca.gov (fastest method)
• By phone: 1-800-300-5616 (English), 1-800-326-8937 (Spanish)

BENEFIT AMOUNT:
• Weekly benefits range from $40 to $450
• Benefits are based on your earnings during the base period
• You may receive benefits for up to 26 weeks

YOUR EMPLOYER PAYS FOR UI:
Your employer pays the entire cost of unemployment insurance. No deductions are taken from your pay.

IMPORTANT:
• You must certify for benefits every two weeks
• Report all earnings when certifying
• It is fraud to collect benefits while working and not reporting your earnings

For more information: www.edd.ca.gov
California Employment Development Department
    `,
  },
  {
    noticeCode: 'CA_PFL_NOTICE',
    title: 'Paid Family Leave (PFL) Notice',
    legalReference: 'California Unemployment Insurance Code Section 3301',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: PAID FAMILY LEAVE BENEFITS

Paid Family Leave (PFL) provides up to 8 weeks of partial pay to eligible workers who need time off work to:
• Care for a seriously ill family member
• Bond with a new child (birth, adoption, or foster care)
• Participate in a qualifying military event

ELIGIBILITY:
You may be eligible if you:
• Have paid into State Disability Insurance (SDI) in the past 5-18 months
• Are unable to do your regular work because you need to care for a seriously ill family member or bond with a new child

FAMILY MEMBERS INCLUDE:
• Child, parent, parent-in-law, grandparent, grandchild, sibling
• Spouse or registered domestic partner

BENEFIT AMOUNT:
• Approximately 60-70% of your weekly wages (up to a maximum)
• Benefits are paid by the State of California, not your employer

HOW TO FILE:
• Online: www.edd.ca.gov
• By mail: File form DE 2501F

JOB PROTECTION:
• PFL does not provide job protection by itself
• However, your job may be protected under CFRA, FMLA, or other laws
• Check with your employer about additional leave protections

SDI DEDUCTIONS:
You contribute to this program through State Disability Insurance (SDI) payroll deductions.

For more information: www.edd.ca.gov/disability/paid-family-leave/
    `,
  },
  {
    noticeCode: 'CA_SDI_NOTICE',
    title: 'State Disability Insurance (SDI) Notice',
    legalReference: 'California Unemployment Insurance Code Section 2601',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: STATE DISABILITY INSURANCE BENEFITS

State Disability Insurance (SDI) provides short-term benefit payments to eligible workers who have a full or partial loss of wages due to a non-work-related illness, injury, or pregnancy.

ELIGIBILITY:
You may be eligible if you:
• Are unable to do your regular work for at least 8 consecutive days
• Have lost wages due to your disability
• Have earned at least $300 in wages during the base period
• Are under the care of a licensed physician/practitioner

BENEFIT AMOUNT:
• Approximately 60-70% of your weekly wages (up to a maximum)
• Benefits begin after a 7-day waiting period

DURATION:
• You can receive SDI benefits for up to 52 weeks

HOW TO FILE:
• Online: www.edd.ca.gov (fastest method)
• By mail: File form DE 2501

SDI DEDUCTIONS:
• SDI is funded through employee payroll deductions
• You will see "CASDI" or "SDI" deducted from your paycheck
• The current SDI rate is 1.1% of your wages (up to the taxable wage limit)

PREGNANCY:
• SDI covers pregnancy-related disabilities
• Most women can receive 4 weeks before and 6-8 weeks after delivery

For more information: www.edd.ca.gov/disability/
California Employment Development Department
    `,
  },
  {
    noticeCode: 'CA_DV_LEAVE',
    title: 'Domestic Violence, Sexual Assault, and Stalking Leave Notice',
    legalReference: 'California Labor Code Sections 230 and 230.1',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: RIGHTS OF VICTIMS OF DOMESTIC VIOLENCE, SEXUAL ASSAULT, AND STALKING

California law provides protections and leave for employees who are victims of domestic violence, sexual assault, or stalking.

TIME OFF FOR COURT PROCEEDINGS:
You are entitled to take time off to:
• Obtain a restraining order or other court order
• Attend court proceedings related to domestic violence, sexual assault, or stalking

TIME OFF FOR TREATMENT AND SAFETY:
You are entitled to take time off to:
• Seek medical attention for injuries
• Obtain services from a domestic violence shelter or program
• Obtain psychological counseling
• Participate in safety planning and relocation

ADVANCE NOTICE:
• Provide reasonable advance notice when possible
• If advance notice is not possible, provide documentation within a reasonable time

DOCUMENTATION:
Your employer may require documentation such as:
• Police report
• Court order or documentation from court
• Documentation from a medical professional, advocate, or counselor

RETALIATION PROHIBITED:
Your employer cannot discharge, discriminate, or retaliate against you for:
• Being a victim of domestic violence, sexual assault, or stalking
• Taking time off for authorized purposes
• Requesting reasonable accommodations for your safety

CONFIDENTIALITY:
Your employer must maintain the confidentiality of your information.

REASONABLE ACCOMMODATIONS:
You may request reasonable accommodations for your safety at work, such as:
• Transfer, reassignment, or modified schedule
• Changed work telephone number
• Changed work station
• Installed lock or other safety device

For more information, contact the California Labor Commissioner:
www.dir.ca.gov/dlse or 1-866-924-9757
    `,
  },
  {
    noticeCode: 'CA_EMERGENCY_INFO',
    title: 'Emergency Information & Disaster Service Worker Notice',
    legalReference: 'California Government Code Section 3100',
    category: 'state_notices',
    jurisdiction: 'CA',
    requiresAck: true,
    requiresSignature: false,
    providedAt: 'hire',
    content: `
NOTICE TO EMPLOYEES: EMERGENCY INFORMATION

DISASTER SERVICE WORKERS:
Under California Government Code Section 3100, all public employees are declared to be disaster service workers, subject to disaster service activities as may be assigned by their superiors or by law.

EMERGENCY CONTACT INFORMATION:
Please ensure your emergency contact information is current and on file with Human Resources.

EMERGENCY PROCEDURES:
• Familiarize yourself with building evacuation routes
• Know the location of emergency exits, fire extinguishers, and first aid kits
• Follow instructions from emergency personnel during drills and actual emergencies

WORKPLACE SAFETY:
• Report any unsafe conditions to your supervisor immediately
• Know your company's emergency procedures
• Participate in required safety training

EMERGENCY CONTACTS:
• Police/Fire/Ambulance: 911
• Poison Control: 1-800-222-1222
• OSHA (workplace safety): 1-800-321-OSHA (6742)

Please update your emergency contact information with HR if any changes occur.
    `,
  },
];

// ============================================================================
// CALIFORNIA POLICY ACKNOWLEDGMENTS (Require E-Signature)
// ============================================================================

export const CA_POLICY_ACKNOWLEDGMENTS = [
  {
    policyCode: 'AT_WILL',
    title: 'At-Will Employment Acknowledgment',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
AT-WILL EMPLOYMENT ACKNOWLEDGMENT

I understand and acknowledge that my employment with [Company Name] is "at-will." This means that either the Company or I may terminate the employment relationship at any time, with or without cause, and with or without notice.

I understand that:

1. No manager, supervisor, or representative of the Company has the authority to enter into any agreement for employment for any specified period of time or to make any agreement contrary to the at-will nature of my employment.

2. Only the [CEO/President] of the Company has the authority to make any such agreement, and any such agreement must be in writing and signed by [CEO/President].

3. Nothing in any employee handbook, policy manual, or other Company document is intended to create or imply a contract of employment or any other contractual rights.

4. The at-will nature of my employment cannot be changed except in writing signed by the [CEO/President].

5. This at-will employment relationship exists regardless of any other written or oral statements or promises made to me.

I have read this acknowledgment, understand its contents, and agree to its terms.
    `,
  },
  {
    policyCode: 'ANTI_HARASSMENT',
    title: 'Anti-Harassment, Discrimination & Retaliation Policy',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
ANTI-HARASSMENT, DISCRIMINATION & RETALIATION POLICY ACKNOWLEDGMENT

[Company Name] is committed to providing a work environment free of unlawful harassment, discrimination, and retaliation.

PROTECTED CHARACTERISTICS:
Company policy prohibits harassment, discrimination, or retaliation based on:
• Race, color, national origin, ancestry
• Religion, creed
• Sex, gender, gender identity, gender expression
• Sexual orientation
• Marital status
• Age (40 and over)
• Physical or mental disability
• Medical condition, genetic information
• Military or veteran status
• Pregnancy, childbirth, or related medical conditions
• Any other characteristic protected by law

HARASSMENT INCLUDES:
• Verbal: Slurs, jokes, epithets, negative stereotyping
• Physical: Assault, blocking movement, unwanted touching
• Visual: Posters, cartoons, drawings, gestures
• Sexual: Unwanted advances, requests for favors, suggestive comments

REPORTING PROCEDURES:
If you experience or witness harassment, discrimination, or retaliation:
1. Report immediately to your supervisor, HR, or any manager
2. Reports may also be made to the California Department of Fair Employment and Housing (DFEH)

NO RETALIATION:
The Company strictly prohibits retaliation against anyone who reports harassment or participates in an investigation.

INVESTIGATION:
All complaints will be investigated promptly, thoroughly, and impartially. Confidentiality will be maintained to the extent possible.

By signing below, I acknowledge that I have read and understand this policy, agree to comply with it, and will report any violations.
    `,
  },
  {
    policyCode: 'MEAL_REST_PERIODS',
    title: 'Meal & Rest Period Policy',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
MEAL AND REST PERIOD POLICY ACKNOWLEDGMENT

California law requires employers to provide meal and rest periods to non-exempt employees.

MEAL PERIODS:
• First Meal Period: 30 minutes if you work more than 5 hours
  - Must begin no later than the end of your 5th hour of work
• Second Meal Period: 30 minutes if you work more than 10 hours
  - Must begin no later than the end of your 10th hour of work

Meal Period Rules:
• You must be relieved of all duties during your meal period
• You are free to leave the premises during your meal period
• Meal periods are unpaid for non-exempt employees
• You may waive your first meal period if you work no more than 6 hours
• You may waive your second meal period if you work no more than 12 hours AND did not waive your first meal period

REST PERIODS:
• 10 minutes of paid rest for every 4 hours worked (or major fraction thereof)
• Rest periods should be taken in the middle of each work period when practical
• Rest periods are counted as time worked and are paid

Rest Period Schedule:
• 3.5 to 6 hours: One 10-minute rest period
• 6+ to 10 hours: Two 10-minute rest periods
• 10+ to 14 hours: Three 10-minute rest periods

YOUR RESPONSIBILITY:
• You are expected to take your required meal and rest periods
• Do not work through your meal or rest periods unless expressly authorized
• Report any interference with your meal or rest periods to your supervisor or HR

By signing below, I acknowledge that I have read and understand this policy.
    `,
  },
  {
    policyCode: 'OVERTIME_POLICY',
    title: 'Overtime Policy',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
OVERTIME POLICY ACKNOWLEDGMENT

California overtime laws apply to all non-exempt employees.

OVERTIME RATES:
• Time and One-Half (1.5x): 
  - Hours worked over 8 in a workday
  - Hours worked over 40 in a workweek
  - First 8 hours on the 7th consecutive day in a workweek

• Double Time (2x):
  - Hours worked over 12 in a workday
  - Hours worked over 8 on the 7th consecutive day in a workweek

AUTHORIZATION:
• All overtime must be approved in advance by your supervisor
• Working unauthorized overtime is a violation of company policy
• However, all hours worked (authorized or not) will be paid

WORKWEEK:
• Our workweek begins on [Day] at 12:00 AM and ends on [Day] at 11:59 PM

RECORDING TIME:
• You must accurately record all hours worked
• Do not work "off the clock"
• Report any discrepancies in your time records immediately

EXEMPT EMPLOYEES:
This policy does not apply to employees classified as exempt from overtime under California law.

By signing below, I acknowledge that I have read and understand this policy.
    `,
  },
  {
    policyCode: 'SICK_LEAVE_POLICY',
    title: 'Sick Leave Policy',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
SICK LEAVE POLICY ACKNOWLEDGMENT

In accordance with California's Healthy Workplaces, Healthy Families Act and local ordinances, [Company Name] provides paid sick leave to eligible employees.

ACCRUAL:
• You accrue paid sick leave at a rate of [X hours] for every [30] hours worked
• Accrual begins on your first day of employment
• You may begin using accrued sick leave after [90] days of employment

ANNUAL LIMITS:
• You may use up to [40/48] hours of paid sick leave per year
• Unused sick leave carries over (up to [80] hours cap)

ACCEPTABLE USES:
• Your own health condition (illness, injury, medical appointment)
• Care for a family member's health condition
• Preventive care for yourself or a family member
• If you are a victim of domestic violence, sexual assault, or stalking

REQUESTING SICK LEAVE:
• Provide advance notice when foreseeable (medical appointments)
• Notify your supervisor as soon as possible when unforeseeable
• [Company Name] may require documentation for absences of [3+] consecutive days

RATE OF PAY:
Sick leave is paid at your regular rate of pay.

NO RETALIATION:
You will not be retaliated against for using or requesting sick leave.

By signing below, I acknowledge that I have read and understand this policy.
    `,
  },
  {
    policyCode: 'TIMEKEEPING_POLICY',
    title: 'Timekeeping & Attendance Policy',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
TIMEKEEPING AND ATTENDANCE POLICY ACKNOWLEDGMENT

Accurate time records are essential for proper compensation and compliance with labor laws.

YOUR RESPONSIBILITIES:
• Record your own time accurately and completely
• Clock in/out at the beginning and end of each shift
• Clock in/out for unpaid meal periods
• Report time worked immediately; do not save up time to report later
• Never record time for another employee or ask someone to record your time

PROHIBITED PRACTICES:
• Working "off the clock" (before clocking in, after clocking out, or during meal periods)
• Altering, falsifying, or tampering with time records
• Clocking in/out for another employee

CORRECTIONS:
• Report any errors in your time record to your supervisor immediately
• Time corrections require supervisor approval and documentation

ATTENDANCE:
• Be at your workstation ready to work at your scheduled start time
• Notify your supervisor as soon as possible if you will be late or absent
• Excessive absenteeism or tardiness may result in disciplinary action

CONSEQUENCES:
Violation of this policy may result in disciplinary action, up to and including termination.

By signing below, I acknowledge that I have read and understand this policy.
    `,
  },
  {
    policyCode: 'IIPP_ACK',
    title: 'Injury & Illness Prevention Program (IIPP) Acknowledgment',
    category: 'policy',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
INJURY AND ILLNESS PREVENTION PROGRAM (IIPP) ACKNOWLEDGMENT

California Labor Code Section 6401.7 requires every employer to establish, implement, and maintain an effective Injury and Illness Prevention Program (IIPP).

PROGRAM ELEMENTS:
1. Management Commitment: Company management is responsible for implementing and maintaining this program
2. Employee Compliance: All employees must comply with safe work practices
3. Communication: Safety information is communicated through training, meetings, and postings
4. Hazard Assessment: Regular inspections identify workplace hazards
5. Hazard Correction: Identified hazards are corrected in a timely manner
6. Training: Employees receive training on general safety and job-specific hazards
7. Recordkeeping: Safety records are maintained as required

YOUR RESPONSIBILITIES:
• Follow all safety rules and procedures
• Use required personal protective equipment (PPE)
• Report unsafe conditions or hazards immediately
• Report all work-related injuries and illnesses
• Participate in safety training
• Not operate equipment you are not trained to use

REPORTING HAZARDS:
Report unsafe conditions to your supervisor or safety coordinator immediately. You may also submit reports anonymously.

NO RETALIATION:
You will not be retaliated against for reporting safety hazards or injuries.

By signing below, I acknowledge that:
• I have received training on the company's IIPP
• I understand my responsibilities for workplace safety
• I will comply with all safety rules and procedures
    `,
  },
];

// ============================================================================
// CALIFORNIA PRIVACY & AGREEMENTS (E-Signature Required)
// ============================================================================

export const CA_AGREEMENTS = [
  {
    policyCode: 'CCPA_PRIVACY',
    title: 'CCPA Employee Privacy Notice',
    category: 'agreement',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
CALIFORNIA CONSUMER PRIVACY ACT (CCPA) EMPLOYEE PRIVACY NOTICE

This notice describes how [Company Name] collects, uses, and discloses personal information of California employees, job applicants, and contractors in accordance with the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA).

CATEGORIES OF PERSONAL INFORMATION COLLECTED:
• Identifiers: Name, SSN, driver's license, contact information
• Professional/Employment Information: Employment history, qualifications, performance evaluations
• Financial Information: Bank account, tax information, compensation
• Protected Characteristics: Age, gender, race, disability status (for EEO purposes)
• Biometric Information: If applicable (e.g., fingerprints for timekeeping)
• Internet/Electronic Activity: Computer usage, email, system access logs
• Geolocation Data: If applicable (e.g., GPS for company vehicles)
• Sensory Data: If applicable (e.g., security camera footage)
• Inferences: Performance assessments, evaluations

PURPOSES FOR COLLECTION:
• Processing payroll and administering benefits
• Managing employment relationship
• Compliance with legal obligations
• Security and fraud prevention
• Business operations

YOUR RIGHTS:
Under CCPA/CPRA, you have the right to:
• Know what personal information is collected
• Access your personal information
• Request deletion of personal information (subject to exceptions)
• Correct inaccurate personal information
• Opt out of sale/sharing (we do not sell employee data)
• Non-discrimination for exercising your rights

HOW TO EXERCISE YOUR RIGHTS:
Submit requests to HR at [email] or [phone number]

By signing below, I acknowledge that I have received and reviewed this privacy notice.
    `,
  },
  {
    policyCode: 'CONFIDENTIALITY',
    title: 'Confidentiality Agreement',
    category: 'agreement',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
CONFIDENTIALITY AGREEMENT

This Confidentiality Agreement ("Agreement") is entered into between [Company Name] ("Company") and the undersigned employee ("Employee").

1. CONFIDENTIAL INFORMATION DEFINED
"Confidential Information" includes, but is not limited to:
• Trade secrets, inventions, and proprietary information
• Business plans, strategies, and financial information
• Customer and vendor lists, pricing, and contracts
• Employee information and compensation data
• Technical data, software, and specifications
• Marketing plans and research
• Any information marked as "Confidential"

2. OBLIGATIONS
Employee agrees to:
• Hold all Confidential Information in strict confidence
• Not disclose Confidential Information to any third party without authorization
• Use Confidential Information only for Company business purposes
• Take reasonable precautions to prevent unauthorized disclosure
• Return all Confidential Information upon termination of employment

3. EXCEPTIONS
This Agreement does not apply to information that:
• Is publicly available through no fault of Employee
• Is received from a third party without confidentiality restrictions
• Is independently developed by Employee without use of Confidential Information
• Is required to be disclosed by law (with notice to Company when permitted)

4. PROTECTED RIGHTS (CALIFORNIA)
Nothing in this Agreement prohibits Employee from:
• Discussing wages, hours, or working conditions
• Reporting suspected violations of law to government agencies
• Participating in investigations by government agencies
• Disclosing information as protected by whistleblower laws

5. DURATION
This Agreement remains in effect during employment and continues indefinitely after termination for trade secrets, and for [2] years for other Confidential Information.

6. REMEDIES
Employee acknowledges that breach may cause irreparable harm and agrees that Company may seek injunctive relief in addition to other remedies.

By signing below, Employee acknowledges reading, understanding, and agreeing to this Agreement.
    `,
  },
  {
    policyCode: 'NDA_CA',
    title: 'Non-Disclosure Agreement (California)',
    category: 'agreement',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
NON-DISCLOSURE AGREEMENT (CALIFORNIA COMPLIANT)

This Non-Disclosure Agreement ("Agreement") is made between [Company Name] ("Disclosing Party") and the undersigned employee ("Receiving Party").

1. PURPOSE
This Agreement protects Confidential Information disclosed during the employment relationship.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means non-public information that:
• Derives economic value from not being generally known
• Is the subject of reasonable efforts to maintain its secrecy
• Is disclosed by Company or learned through employment

3. EXCLUSIONS
Confidential Information does not include information that:
• Was publicly known prior to disclosure
• Becomes publicly known through no fault of Receiving Party
• Was independently developed without use of Confidential Information
• Was rightfully received from a third party

4. OBLIGATIONS
Receiving Party shall:
• Maintain strict confidentiality
• Use information only for authorized business purposes
• Not copy or reproduce without authorization
• Return all materials upon request or termination

5. CALIFORNIA LAW COMPLIANCE
This Agreement does not restrict Employee's right to:
• Discuss wages, hours, and working conditions (Labor Code § 232)
• Report unlawful activity to government agencies
• Disclose information in legal proceedings regarding harassment or discrimination claims (SB 331)
• Engage in protected concerted activity

6. DEFEND TRADE SECRETS ACT NOTICE
Pursuant to 18 U.S.C. § 1833(b), Employee will not be held criminally or civilly liable under any federal or state trade secret law for disclosure made in confidence to a government official or attorney solely for reporting suspected violations of law.

7. TERM
This Agreement remains effective during employment and for [2] years thereafter, except for trade secrets which are protected indefinitely.

By signing below, Employee agrees to the terms of this Agreement.
    `,
  },
  {
    policyCode: 'ARBITRATION_CA',
    title: 'Arbitration Agreement (Optional - California Compliant)',
    category: 'agreement',
    jurisdiction: 'CA',
    requiresSignature: true,
    isOptional: true,
    content: `
MUTUAL AGREEMENT TO ARBITRATE CLAIMS
(CALIFORNIA COMPLIANT - VOLUNTARY)

IMPORTANT: This arbitration agreement is VOLUNTARY. Your employment is not conditioned on signing this agreement.

This Mutual Agreement to Arbitrate Claims ("Agreement") is between [Company Name] ("Company") and the undersigned employee ("Employee").

1. AGREEMENT TO ARBITRATE
Company and Employee mutually agree to resolve all disputes through final and binding arbitration instead of court litigation, except as stated below.

2. CLAIMS COVERED
This Agreement covers all employment-related claims including:
• Discrimination, harassment, and retaliation claims
• Wage and hour disputes
• Wrongful termination claims
• Contract disputes

3. CLAIMS NOT COVERED
This Agreement does NOT cover:
• Workers' compensation claims
• Unemployment insurance claims
• Claims for injunctive relief
• PAGA representative claims (Labor Code Private Attorneys General Act)
• Claims before administrative agencies (DFEH, DLSE, EEOC)

4. ARBITRATION PROCESS
• Arbitration administered by [JAMS/AAA] under employment arbitration rules
• Location: [City], California
• Arbitrator: Neutral arbitrator mutually selected
• Discovery: Adequate discovery shall be permitted
• Hearing: Employee may appear and present evidence

5. COSTS AND FEES
• Company pays arbitration filing fees and arbitrator costs
• Each party pays own attorney's fees unless otherwise awarded
• Same remedies available as in court

6. CALIFORNIA PROTECTIONS (AB 51 COMPLIANCE)
• This Agreement is VOLUNTARY - not a condition of employment
• Employee may decline without retaliation
• Employee retains right to file administrative charges
• Employee retains right to file PAGA claims

7. OPT-OUT RIGHT
Employee may opt out of this Agreement by providing written notice to HR within 30 days of signing. Opting out will not affect employment.

☐ I AGREE to this Arbitration Agreement
☐ I DECLINE this Arbitration Agreement (Declining has no effect on employment)

By signing below, Employee acknowledges voluntary agreement or declination.
    `,
  },
  {
    policyCode: 'TRADE_SECRETS',
    title: 'Trade Secrets Acknowledgment',
    category: 'agreement',
    jurisdiction: 'CA',
    requiresSignature: true,
    content: `
TRADE SECRETS ACKNOWLEDGMENT

I, the undersigned employee, acknowledge and agree:

1. DEFINITION OF TRADE SECRETS
Trade secrets include information that:
• Derives independent economic value from not being generally known
• Is not readily ascertainable by proper means
• Is subject to reasonable efforts to maintain secrecy

Examples include (but are not limited to):
• Formulas, patterns, compilations, programs, devices, methods, techniques
• Customer lists, pricing information, business strategies
• Technical specifications, software code, product designs

2. ACKNOWLEDGMENT
I acknowledge that during my employment, I may have access to trade secrets and confidential information belonging to [Company Name].

3. AGREEMENT
I agree to:
• Protect trade secrets during and after my employment
• Not disclose trade secrets to anyone without authorization
• Not use trade secrets for personal benefit or to benefit others
• Return all materials containing trade secrets upon termination

4. CALIFORNIA LAW
I understand that California law protects trade secrets under the California Uniform Trade Secrets Act (Civil Code § 3426).

5. DEFEND TRADE SECRETS ACT (DTSA) NOTICE
I understand that pursuant to 18 U.S.C. § 1833(b), I will not be held criminally or civilly liable under any federal or state trade secret law for the disclosure of a trade secret that is made:
• In confidence to a government official or attorney solely for the purpose of reporting or investigating a suspected violation of law; or
• In a complaint or other document filed in a lawsuit if such filing is made under seal.

6. NON-COMPETE NOTICE (CALIFORNIA)
I understand that non-compete agreements are generally void in California under Business and Professions Code § 16600.

By signing below, I acknowledge that I have read, understand, and agree to this acknowledgment.
    `,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all California compliance items for onboarding
 */
export function getCAOnboardingItems() {
  return {
    taxForms: [CA_DE4_FORM],
    wageNotice: CA_WAGE_THEFT_PREVENTION_NOTICE,
    mandatoryNotices: CA_MANDATORY_NOTICES,
    policyAcknowledgments: CA_POLICY_ACKNOWLEDGMENTS,
    agreements: CA_AGREEMENTS,
  };
}

/**
 * Get required notices by jurisdiction
 */
export function getRequiredNoticesByState(stateCode) {
  if (stateCode === 'CA') {
    return CA_MANDATORY_NOTICES;
  }
  // Add other states as needed
  return [];
}

/**
 * Get policy acknowledgments by jurisdiction
 */
export function getPolicyAcknowledgmentsByState(stateCode) {
  if (stateCode === 'CA') {
    return CA_POLICY_ACKNOWLEDGMENTS;
  }
  // Add other states as needed
  return [];
}

/**
 * Get agreements by jurisdiction
 */
export function getAgreementsByState(stateCode) {
  if (stateCode === 'CA') {
    return CA_AGREEMENTS;
  }
  // Add other states as needed
  return [];
}

export default {
  CA_DE4_FILING_STATUS,
  CA_DE4_FORM,
  CA_WAGE_THEFT_PREVENTION_NOTICE,
  CA_MANDATORY_NOTICES,
  CA_POLICY_ACKNOWLEDGMENTS,
  CA_AGREEMENTS,
  getCAOnboardingItems,
  getRequiredNoticesByState,
  getPolicyAcknowledgmentsByState,
  getAgreementsByState,
};
